import { AnyAction, Middleware, ThunkAction } from '@reduxjs/toolkit';

import { AppDispatch, StoreSchema } from './store.schema';

type RunningQuery = {
	abort: () => void;
};

type GetRunningQueryThunk = ThunkAction<RunningQuery | undefined, StoreSchema, unknown, AnyAction>;

type RtkQueryApi = {
	reducerPath: string;
	internalActions: {
		unsubscribeQueryResult: {
			match: (action: AnyAction) => action is AnyAction & {
				payload: {
					queryCacheKey: string;
					requestId: string;
				};
			};
		};
	};
	util: {
		getRunningQueryThunk: (endpointName: string, originalArgs: unknown) => GetRunningQueryThunk;
		resetApiState: {
			match: (action: AnyAction) => boolean;
		};
	};
};

type QueryThunkArg = {
	type: 'query';
	subscribe?: boolean;
	endpointName: string;
	originalArgs: unknown;
	queryCacheKey: string;
};

type QueryAction = AnyAction & {
	meta?: {
		arg?: Partial<QueryThunkArg>;
		condition?: boolean;
		requestId?: string;
	};
};

type QuerySubscription = {
	endpointName: string;
	originalArgs: unknown;
	requestIds: Set<string>;
};

const subscriptionsByApi = new Map<string, Map<string, QuerySubscription>>();

const getApiSubscriptions = (reducerPath: string): Map<string, QuerySubscription> => {
	const existingSubscriptions = subscriptionsByApi.get(reducerPath);

	if (existingSubscriptions) {
		return existingSubscriptions;
	}

	const subscriptions = new Map<string, QuerySubscription>();
	subscriptionsByApi.set(reducerPath, subscriptions);
	return subscriptions;
};

const isQuerySubscriptionStart = (
	api: RtkQueryApi,
	action: QueryAction,
): action is QueryAction & {
	meta: { arg: QueryThunkArg; requestId: string; condition?: boolean };
} => {
	const isPendingQuery = action.type === `${api.reducerPath}/executeQuery/pending`;
	const isSkippedSubscribedQuery =
		action.type === `${api.reducerPath}/executeQuery/rejected` && action.meta?.condition === true;
	const arg = action.meta?.arg;

	return Boolean(
		(isPendingQuery || isSkippedSubscribedQuery) &&
			arg?.type === 'query' &&
			arg.subscribe &&
			arg.endpointName &&
			arg.queryCacheKey &&
			action.meta?.requestId,
	);
};

const trackQuerySubscription = (api: RtkQueryApi, action: QueryAction) => {
	if (!isQuerySubscriptionStart(api, action)) {
		return;
	}

	const { endpointName, originalArgs, queryCacheKey } = action.meta.arg;
	const apiSubscriptions = getApiSubscriptions(api.reducerPath);
	const subscription = apiSubscriptions.get(queryCacheKey) ?? {
		endpointName,
		originalArgs,
		requestIds: new Set<string>(),
	};

	subscription.requestIds.add(action.meta.requestId);
	apiSubscriptions.set(queryCacheKey, subscription);
};

const abortIfLastSubscriberLeft = (api: RtkQueryApi, action: AnyAction, dispatch: AppDispatch) => {
	if (!api.internalActions.unsubscribeQueryResult.match(action)) {
		return;
	}

	const apiSubscriptions = getApiSubscriptions(api.reducerPath);
	const subscription = apiSubscriptions.get(action.payload.queryCacheKey);

	if (!subscription) {
		return;
	}

	subscription.requestIds.delete(action.payload.requestId);

	if (subscription.requestIds.size > 0) {
		return;
	}

	apiSubscriptions.delete(action.payload.queryCacheKey);
	dispatch(api.util.getRunningQueryThunk(subscription.endpointName, subscription.originalArgs))?.abort();
};

export const createAbortUnusedQueryMiddleware = (
	rtkQueryApis: RtkQueryApi[],
): Middleware<unknown, StoreSchema, AppDispatch> => {
	return ({ dispatch }) =>
		(next) =>
		(action: AnyAction) => {
			for (const api of rtkQueryApis) {
				trackQuerySubscription(api, action);
			}

			const result = next(action);

			for (const api of rtkQueryApis) {
				if (api.util.resetApiState.match(action)) {
					subscriptionsByApi.delete(api.reducerPath);
				} else {
					abortIfLastSubscriberLeft(api, action, dispatch);
				}
			}

			return result;
		};
};
